/**
 * SwipeableListItem.js
 * 모바일용 스와이프 가능한 리스트 아이템 컴포넌트
 * 좌우 스와이프로 액션 버튼 표시
 */

import React, { useState, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { Trash2, Edit2, CheckCircle, Clock } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const SwipeableListItem = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  onDelete,
  onEdit,
  onComplete,
  onConvert,
  leftActions = [],
  rightActions = [],
  threshold = 100, // 스와이프 임계값 (px)
  ...props
}) => {
  const { colors, isDark } = useCustomTheme();
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const itemRef = useRef(null);
  const touchStartTarget = useRef(null); // 터치 시작 타겟 저장

  const handleTouchStart = (e) => {
    // 터치 시작 타겟 저장
    touchStartTarget.current = e.target;
    
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    setIsDragging(true);
    // 햅틱 피드백 (시작)
    if (navigator.vibrate) {
      navigator.vibrate(5);
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    currentX.current = e.touches[0].clientX;
    const deltaX = currentX.current - startX.current;
    
    // 스와이프 제한 (최대 120px)
    const maxSwipe = 120;
    const newTranslateX = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
    setTranslateX(newTranslateX);
    
    // 임계값 근처에서 햅틱 피드백
    if (Math.abs(deltaX) > threshold - 10 && Math.abs(deltaX) < threshold + 10) {
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const deltaX = currentX.current - startX.current;
    
    // 임계값 이상 스와이프 시 액션 실행
    if (Math.abs(deltaX) > threshold) {
      // 햅틱 피드백 (액션 실행)
      if (navigator.vibrate) {
        navigator.vibrate(20);
      }
      
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
    
    // 원래 위치로 복귀 (부드러운 애니메이션)
    setTranslateX(0);
    setIsDragging(false);
  };

  // 기본 액션 버튼 생성
  const getDefaultActions = () => {
    const actions = [];
    
    if (onEdit) {
      actions.push({
        icon: Edit2,
        label: '수정',
        onClick: onEdit,
        color: colors.accent,
      });
    }
    
    if (onComplete) {
      actions.push({
        icon: CheckCircle,
        label: '완료',
        onClick: onComplete,
        color: '#4CAF50',
      });
    }
    
    if (onConvert) {
      actions.push({
        icon: Clock,
        label: '투입시간 반영',
        onClick: onConvert,
        color: colors.accent,
      });
    }
    
    if (onDelete) {
      actions.push({
        icon: Trash2,
        label: '삭제',
        onClick: onDelete,
        color: colors.error || '#f44336',
      });
    }
    
    return actions;
  };

  const leftActionsList = leftActions.length > 0 ? leftActions : [];
  const rightActionsList = rightActions.length > 0 ? rightActions : getDefaultActions();

  return (
    <Box
      ref={itemRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'pan-y',
        userSelect: 'none',
        ...props.sx,
      }}
      onTouchStart={(e) => {
        // 체크박스나 다른 인터랙티브 요소에서 시작된 터치는 무시
        const target = e.target;
        
        // 이벤트가 이미 stopPropagation되었는지 확인
        if (e.defaultPrevented || e.cancelBubble) {
          setIsDragging(false);
          return;
        }
        
        // MUI Checkbox는 span 요소로 렌더링되므로 더 넓은 범위로 체크
        const checkboxElement = target.closest('input[type="checkbox"]') || 
                                 target.closest('[role="checkbox"]') ||
                                 target.closest('span[class*="MuiCheckbox"]') ||
                                 target.closest('span[class*="PrivateSwitchBase"]') ||
                                 target.closest('button') ||
                                 target.closest('[data-checkbox-container]'); // 체크박스 컨테이너 마커
        
        if (checkboxElement) {
          // 체크박스 관련 요소에서 시작된 터치는 스와이프로 처리하지 않음
          setIsDragging(false);
          touchStartTarget.current = null;
          return;
        }
        
        handleTouchStart(e);
      }}
      onTouchMove={(e) => {
        // 드래깅이 시작되지 않았으면 무시
        if (!isDragging || !touchStartTarget.current) {
          return;
        }
        
        // 터치 시작 타겟이 체크박스 관련 요소인지 확인
        const startTarget = touchStartTarget.current;
        const checkboxElement = startTarget.closest('input[type="checkbox"]') || 
                                 startTarget.closest('[role="checkbox"]') ||
                                 startTarget.closest('span[class*="MuiCheckbox"]') ||
                                 startTarget.closest('span[class*="PrivateSwitchBase"]') ||
                                 startTarget.closest('button') ||
                                 startTarget.closest('[data-checkbox-container]');
        
        if (checkboxElement) {
          // 체크박스 관련 요소에서 시작된 터치는 스와이프로 처리하지 않음
          setIsDragging(false);
          setTranslateX(0);
          touchStartTarget.current = null;
          return;
        }
        
        handleTouchMove(e);
      }}
      onTouchEnd={(e) => {
        // 드래깅이 시작되지 않았으면 무시
        if (!isDragging || !touchStartTarget.current) {
          touchStartTarget.current = null;
          return;
        }
        
        // 터치 시작 타겟이 체크박스 관련 요소인지 확인
        const startTarget = touchStartTarget.current;
        const checkboxElement = startTarget.closest('input[type="checkbox"]') || 
                                 startTarget.closest('[role="checkbox"]') ||
                                 startTarget.closest('span[class*="MuiCheckbox"]') ||
                                 startTarget.closest('span[class*="PrivateSwitchBase"]') ||
                                 startTarget.closest('button') ||
                                 startTarget.closest('[data-checkbox-container]');
        
        if (checkboxElement) {
          // 체크박스 관련 요소에서 시작된 터치는 스와이프로 처리하지 않음
          setIsDragging(false);
          setTranslateX(0);
          touchStartTarget.current = null;
          return;
        }
        
        handleTouchEnd(e);
        touchStartTarget.current = null;
      }}
    >
      {/* 좌측 액션 버튼 */}
      {leftActionsList.length > 0 && translateX > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
            zIndex: 1,
            paddingX: 1,
            gap: 0.5,
          }}
        >
          {leftActionsList.map((action, index) => (
            <IconButton
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                // 햅틱 피드백
                if (navigator.vibrate) {
                  navigator.vibrate(15);
                }
                action.onClick();
                setTranslateX(0);
              }}
              sx={{
                color: action.color || colors.accent,
                minWidth: '56px',
                minHeight: '56px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.2s ease',
                '&:active': {
                  transform: 'scale(0.9)',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                },
              }}
            >
              <action.icon size={22} />
            </IconButton>
          ))}
        </Box>
      )}

      {/* 메인 컨텐츠 */}
      <Box
        sx={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: colors.secondaryBg,
          position: 'relative',
          zIndex: 2,
          willChange: 'transform',
        }}
      >
        {children}
      </Box>

      {/* 우측 액션 버튼 */}
      {rightActionsList.length > 0 && translateX < 0 && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
            zIndex: 1,
            paddingX: 1,
            gap: 0.5,
          }}
        >
          {rightActionsList.map((action, index) => (
            <IconButton
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                // 햅틱 피드백
                if (navigator.vibrate) {
                  navigator.vibrate(15);
                }
                action.onClick();
                setTranslateX(0);
              }}
              sx={{
                color: action.color || colors.accent,
                minWidth: '56px',
                minHeight: '56px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.2s ease',
                '&:active': {
                  transform: 'scale(0.9)',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                },
              }}
            >
              <action.icon size={22} />
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SwipeableListItem;

